/**
 * useElementWidth (HOF-96) — the measured content width of one element, in px.
 *
 * Added for an axis decision that genuinely depends on width: a twelve-label
 * monthly ladder fits a desktop plot and plainly does not fit a 320 px one
 * (`MONTHLY_TICKS_MIN_PLOT_PX` in `PriceChart.tsx`), so the chart has to know
 * how wide it actually is. A CSS media query could not answer it — the plot is
 * a flex child inside a page shell with a lens panel, so the viewport width and
 * the plot width are two different numbers.
 *
 * **Zero means "not measured yet", and that is a value, not a failure.** It is
 * what the first paint returns, what jsdom returns, and what an environment
 * with no `ResizeObserver` returns after an unlaid-out mount. Every consumer has
 * to have an answer for it — which is why the width gate is written as "dense
 * ladder ABOVE a threshold" rather than "sparse ladder below one": 0 falls on
 * the conservative side by construction.
 *
 * A ref CALLBACK rather than a `useRef` + `useEffect` pair, because the element
 * this was written for mounts conditionally (the plot renders only when there is
 * something to draw). An effect keyed on a ref object cannot see that node
 * appear; a callback ref is called exactly when it does.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface ElementWidth<T extends HTMLElement> {
  /** Attach to the element to measure. */
  ref: (node: T | null) => void;
  /** Its content width in px, or 0 while nothing has been measured. */
  width: number;
}

export function useElementWidth<T extends HTMLElement>(): ElementWidth<T> {
  const [width, setWidth] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    // Always tear the previous observation down first: a callback ref fires
    // again with a new node whenever React swaps the element, and an observer
    // left attached to the old one keeps reporting a width nothing is drawn at.
    observer.current?.disconnect();
    observer.current = null;
    if (!node) {
      setWidth(0);
      return;
    }
    // Measure at mount rather than waiting for the first observation, so the
    // first paint already has a usable number instead of flickering through the
    // fallback. In jsdom this is 0, which is the correct answer there.
    setWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    ro.observe(node);
    observer.current = ro;
  }, []);

  // The unmount path. React calls `ref(null)` on unmount, which already
  // disconnects — this is the belt for the case it does not (a StrictMode
  // double-invoke, an error boundary tearing the tree down mid-commit), because
  // a leaked observer holds the detached node for as long as the page lives.
  useEffect(() => () => observer.current?.disconnect(), []);

  return { ref, width };
}
